/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable indent */
import React, { useCallback, useMemo } from "react";
import _ from "lodash/fp";

import { default as IngkaSelect, Option as SelectOption } from "@ingka/select";
import { OverflowCarousel } from "@ingka/carousel";
import styled from "styled-components";
import SSRIcon from "@ingka/ssr-icon";
import Switch from "@ingka/switch";
import Button from "@ingka/button";
import InputField from "@ingka/input-field";
import {
	colourSemanticNegative,
	colourStaticDarkGrey,
	colourStaticGrey,
	colourStaticLightGrey,
	colourTextAndIcon5,
} from "@ingka/variables/colours-css";
import ProductIdentifier from "@ingka/product-identifier";
import plusSmall from "@ingka/ssr-icon/paths/plus-small";
import crossSmall from "@ingka/ssr-icon/paths/cross-small";
import FormField from "@ingka/form-field";
import Combobox, { MenuItem } from "@ingka/combobox";

import { Schema, SchemaMenuOption } from "../../utils/types";
import * as helpers from "../../utils/helpers";
import { Input } from "../Input";
import { Checkbox } from "../Checkbox";

interface ItemProps {
	onDelete: () => void;
	children: React.ReactNode;
	isEnabled: boolean;
	label?: string;
}

interface ItemWrapperprops {
	isEnabled?: boolean;
}

const ItemContainer = styled.div<ItemWrapperprops>`
	padding: 1rem;
	border-radius: 0.25rem;
	border-top: 1px solid ${colourStaticGrey};
	border-right: 1px solid ${colourStaticGrey};
	border-bottom: 1px solid ${colourStaticGrey};
	border-left: 0.25rem solid ${colourStaticGrey};
	box-shadow: 0 0.25rem 1rem #0000001a;
	font-size: 0.875rem;
	line-height: 1.571;
	.switch {
		margin-bottom: 0.5rem;
	}
	${({ isEnabled }) =>
		isEnabled
			? "border-inline-start-color: rgb(0, 88, 163)"
			: `border-inline-start-color: ${colourStaticDarkGrey};  border-right: 1px solid ${colourStaticGrey}`};
`;

const Item: React.FunctionComponent<ItemProps> = ({ children, onDelete, isEnabled, label }) => {
	return (
		<ItemContainer isEnabled={isEnabled}>
			<Switch label={label} id={label || "switch"} value={label || "switch"} onChange={onDelete} checked={isEnabled} />
			{children}
		</ItemContainer>
	);
};

type Props = {
	field: SchemaMenuOption;
	schema: Schema;
	onChange: (schema: Schema) => void;
	isEnabled: boolean;
};

export const TextItem: React.FunctionComponent<Props> = ({ field, schema, onChange, isEnabled }: Props) => {
	return (
		<Item
			onDelete={() =>
				isEnabled
					? onChange(helpers.deleteSchemaField(field.value, schema))
					: onChange(helpers.setSchemaField(field.value, undefined, schema))
			}
			isEnabled={isEnabled}
			label={field?.label}
		>
			<Input
				id={field.value}
				label={field?.label}
				value={helpers.getSchemaField(field.value, schema) as string}
				onChange={(text) => onChange(helpers.setSchemaField(field.value, text, schema))}
				disabled={!isEnabled}
				placeholder={field?.label ? `Please provide ${field?.label.toLowerCase()}` : ""}
			/>
		</Item>
	);
};

export const NumberItem: React.FunctionComponent<Props> = ({ field, schema, onChange, isEnabled }: Props) => {
	return (
		<Item
			onDelete={() =>
				isEnabled
					? onChange(helpers.deleteSchemaField(field.value, schema))
					: onChange(helpers.setSchemaField(field.value, undefined, schema))
			}
			isEnabled={isEnabled}
			label={field?.label}
		>
			<Input
				id={field.value}
				type={"number"}
				label={field?.label}
				value={helpers.getSchemaField(field.value, schema) as string}
				onChange={(text) => {
					onChange(helpers.setSchemaField(field.value, text === "" ? 0 : parseInt(text, 10), schema));
				}}
				disabled={!isEnabled}
				placeholder={
					field?.label
						? `Please provide ${field?.label.toLowerCase()} ${field?.label.includes("Value") ? "" : "value"}`
						: ""
				}
			/>
		</Item>
	);
};

export const BoolItem: React.FunctionComponent<Props> = ({ field, schema, onChange, isEnabled }: Props) => {
	return (
		<Item
			onDelete={() =>
				isEnabled
					? onChange(helpers.deleteSchemaField(field.value, schema))
					: onChange(helpers.setSchemaField(field.value, false, schema))
			}
			isEnabled={isEnabled}
			label={field?.label}
		>
			<Checkbox
				value={(helpers.getSchemaField(field.value, schema) as boolean) || false}
				label={field?.label}
				onChange={(text) => onChange(helpers.setSchemaField(field.value, text, schema))}
				disabled={!isEnabled}
				placeholder={field?.label ? `Please provide ${field?.label.toLowerCase()}` : ""}
			/>
		</Item>
	);
};

const FlexWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 0.25rem;
	.btn {
		margin-top: 1.5rem;
	}
`;

const OptionWrapper = styled.div`
	display: flex;
	gap: 0.25rem;
	flex-wrap: wrap;
`;

const IconWrapper = styled.div`
	background-color: ${colourStaticLightGrey};
	height: 1.4rem;
	border-left: 1px solid ${colourStaticGrey};
	cursor: pointer;
	display: flex;
	align-items: center;
	:hover {
		background-color: ${colourSemanticNegative};
		path:first-child {
			fill: ${colourTextAndIcon5};
		}
	}
`;

const InputWrapper = styled.div`
	width: 100%;
`;

const AddButtonWrapper = styled.div`
	margin-bottom: 1.25rem;
`;

export const CreatableMultiSelectItem: React.FunctionComponent<Props> = ({
	field,
	schema,
	onChange,
	isEnabled,
}: Props) => {
	const selected = React.useMemo(() => helpers.getSchemaField(field.value, schema) || [], [field.value, schema]);

	const [currentData, setCurrentData] = React.useState<string>("");

	const onAdd = useCallback(() => {
		selected &&
			!(selected as string[])?.includes(currentData) &&
			currentData &&
			onChange(helpers.setSchemaField(field.value, [...(selected as string[]), currentData], schema));
		!(selected as string[])?.includes(currentData) && currentData && setCurrentData("");
	}, [currentData, field.value, onChange, schema, selected]);

	const onRemove = useCallback(
		(value: string) => {
			onChange(helpers.setSchemaField(field.value, _.difference(selected as string[], [value]), schema));
		},
		[field.value, onChange, schema, selected]
	);

	return (
		<Item
			onDelete={() =>
				isEnabled
					? onChange(helpers.deleteSchemaField(field.value, schema))
					: onChange(helpers.setSchemaField(field.value, undefined, schema))
			}
			isEnabled={isEnabled}
			label={field?.label}
		>
			<div>
				<FlexWrapper>
					<InputWrapper>
						<FormField>
							<InputField
								id={field.label}
								label={field.label}
								type="text"
								value={currentData}
								onChange={(e) => {
									setCurrentData(e.target.value);
								}}
								disabled={!isEnabled}
							/>
						</FormField>
					</InputWrapper>
					<AddButtonWrapper>
						<Button text="Add value" size="small" iconOnly ssrIcon={plusSmall} onClick={onAdd} disabled={!isEnabled} />
					</AddButtonWrapper>
				</FlexWrapper>

				<OptionWrapper>
					{(selected as string[])?.length
						? (selected as string[]).map((val: any) => {
								return (
									<div key={val} style={{ display: "flex" }}>
										<ProductIdentifier key={val} subtle value={val} />
										<IconWrapper onClick={() => onRemove(val)}>
											<SSRIcon paths={crossSmall} color={colourStaticDarkGrey} />
										</IconWrapper>
									</div>
								);
						  })
						: null}
				</OptionWrapper>
			</div>
		</Item>
	);
};

interface SelectedOption {
	value: string;
	label: string;
}

const SelectItemWrapper = styled.div`
	width: 100%;
`;

export const SelectItem: React.FunctionComponent<Props> = ({ field, schema, onChange, isEnabled }: Props) => {
	const options = field.optionList;

	const option = helpers.getSchemaField(field.value, schema);

	const selected = React.useMemo(() => helpers.findOption(option as string)(options), [options, option]);

	return (
		<Item
			onDelete={() =>
				isEnabled
					? onChange(helpers.deleteSchemaField(field.value, schema))
					: onChange(helpers.setSchemaField(field.value, undefined, schema))
			}
			isEnabled={isEnabled}
			label={field?.label}
		>
			<SelectItemWrapper>
				<IngkaSelect
					id={field.value}
					label={field.value}
					value={(selected as unknown as SelectedOption)?.value}
					onChange={(e: any) => {
						onChange(helpers.setSchemaField(field.value, e.target.value, schema));
					}}
					disabled={!isEnabled}
				>
					{options.map((data: SelectedOption) => (
						<SelectOption id={data.value} value={data.value} name={data.label} />
					))}
				</IngkaSelect>
			</SelectItemWrapper>
		</Item>
	);
};

export const RequiredMultiSelectItem: React.FunctionComponent<Props> = ({ field, schema, onChange, isEnabled }) => {
	const requiredFields = schema?.required as string[];
	const [selectedOptions, setSelectedOptions] = React.useState<string[]>(requiredFields ? requiredFields : []);

	const helperMessage = useMemo(
		() => (selectedOptions.length > 0 ? `Selected: ${selectedOptions.join(", ")}` : ""),
		[selectedOptions]
	);

	if (!helpers.isSchemaObject(schema) || !helpers.hasSchemaProperties(schema)) return null;

	const allOptions = helpers.schemaPropertiesAsOptions(schema);

	return (
		<Item
			onDelete={() =>
				isEnabled
					? onChange(helpers.deleteSchemaField(field.value, schema))
					: onChange(helpers.setSchemaField(field.value, undefined, schema))
			}
			isEnabled={isEnabled}
			label={field?.label}
		>
			<FormField
				fieldHelper={{
					msg: helperMessage,
					id: "requiredFieldsHelperText",
				}}
			>
				<Combobox
					label={field.label}
					listboxId="requiredFileds"
					labelId="requiredFields"
					disabled={!isEnabled}
					value={selectedOptions}
					multiple
					onListBoxChange={(selectedItems) => {
						setSelectedOptions(selectedItems);
						onChange(helpers.setSchemaField(field.value, selectedItems, schema));
					}}
					placeholder="Choose an option"
					onClearClicked={() => {
						setSelectedOptions([]);
						onChange(helpers.setSchemaField(field.value, [], schema));
					}}
				>
					{allOptions?.map(({ value = "" }, index: number) => {
						return <MenuItem key={`${value}-${index}`} id={value} title={value as string} />;
					})}
				</Combobox>
			</FormField>
		</Item>
	);
};
